import type { IntlShape, MessageDescriptor } from 'react-intl';

import type { ToolCallDisplay } from '../../../contracts/tool-call-display';
import { TOOL_PRESENTATION_REGISTRY } from '../../../data/projects/timeline/presentation/tool-registry';
import type { BlockStatus } from '../../../data/projects/timeline/presentation/types';
import { toolCallLabelMessages } from '../../../data/projects/timeline/toolCallLabel.messages';

type InlineToolCallLabelArgs = {
  toolName: string;
  status: BlockStatus;
  input?: Record<string, unknown>;
  toolCallDisplay?: ToolCallDisplay;
  /** Number of same-name calls when labelling a group; omit/1 for a single row. */
  count?: number;
  /** Group only: calls that finished successfully, when status is 'error' | 'cancelled'. */
  done?: number;
};

const previewToolInput = (input: Record<string, unknown>): string | undefined => {
  for (const key of ['query', 'q', 'search', 'url', 'title', 'prompt', 'name'] as const) {
    const value = input[key];
    if (typeof value === 'string' && value.trim()) return truncate(value.trim(), 120);
  }
  return undefined;
};

const truncate = (s: string, max: number): string =>
  s.length <= max ? s : `${s.slice(0, max - 1)}…`;

const SESSION_RECORDING_LABELS: Record<string, MessageDescriptor> = {
  start: toolCallLabelMessages.transcribingStarted,
  resume: toolCallLabelMessages.transcribingResumed,
  pause: toolCallLabelMessages.transcribingPaused,
  stop: toolCallLabelMessages.transcribingStopped,
};

const isGroup = (count: number | undefined): count is number =>
  count !== undefined && count >= 2;

const wrapTerminal = (intl: IntlShape, status: BlockStatus, label: string): string =>
  intl.formatMessage(
    status === 'error'
      ? toolCallLabelMessages.toolFailed
      : toolCallLabelMessages.toolCancelled,
    { label }
  );

const withPreview = (base: string, preview: string | undefined): string =>
  preview ? `${base}: ${preview}` : base;

/**
 * The title the backend substitutes when the model wrote none: the tool name,
 * humanised (`_tool_display_title_from_name` in ml-scribe's
 * `tool_call_display.py`). A title equal to it was not written for this call.
 */
const standInTitle = (toolName: string): string => {
  const words = toolName
    .split(/[._:\-\s]+/)
    .filter(Boolean)
    .join(' ');
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Run tool';
};

/**
 * One clinician-facing label for an inline tool step or a same-name group.
 *
 * A single call is named in the present tense by the title the model wrote for
 * it, which says what this call is doing where the catalog only says what the
 * tool does; the catalog supplies the past tense the title cannot. Otherwise the
 * precedence is catalog row → backend `toolCallDisplay.title` (single rows) →
 * localised generic. A backend-title-only group falls through to the generic
 * rather than a `{title} · N×` form.
 */
export const inlineToolCallLabel = (
  intl: IntlShape,
  args: InlineToolCallLabelArgs
): string => {
  const { toolName, status, input = {}, toolCallDisplay, count, done = 0 } = args;

  if (toolName === 'ui.session_recording') {
    const action = typeof input.action === 'string' ? input.action : '';
    return intl.formatMessage(
      SESSION_RECORDING_LABELS[action] ?? toolCallLabelMessages.transcribing
    );
  }

  const grouped = isGroup(count);
  const preset = TOOL_PRESENTATION_REGISTRY[toolName]?.label;
  const catalogOnly = preset?.textMode === 'catalog-only';
  const preview = grouped || catalogOnly ? undefined : previewToolInput(input);
  const beTitle = catalogOnly ? undefined : toolCallDisplay?.title.trim();
  const writtenTitle =
    beTitle && beTitle !== standInTitle(toolName) ? beTitle : undefined;

  if (preset) {
    if (grouped) {
      if (status === 'running') return intl.formatMessage(preset.active, { count });
      if (status === 'success') return intl.formatMessage(preset.past, { count });
      if (done > 0) return intl.formatMessage(preset.pastPartial, { done, count });
      return wrapTerminal(intl, status, intl.formatMessage(preset.active, { count }));
    }

    const active = writtenTitle ?? intl.formatMessage(preset.active, { count: 1 });
    if (status === 'running') return withPreview(active, preview);
    if (status === 'success') {
      return withPreview(intl.formatMessage(preset.past, { count: 1 }), preview);
    }
    return withPreview(wrapTerminal(intl, status, active), preview);
  }

  if (beTitle && !grouped) {
    if (status === 'running' || status === 'success') {
      return withPreview(beTitle, preview);
    }
    return withPreview(wrapTerminal(intl, status, beTitle), preview);
  }

  if (grouped) {
    if (status === 'running') {
      return intl.formatMessage(toolCallLabelMessages.toolsRunning, { count });
    }
    if (status === 'success') {
      return intl.formatMessage(toolCallLabelMessages.toolsRan, { count });
    }
    return wrapTerminal(
      intl,
      status,
      intl.formatMessage(toolCallLabelMessages.toolsRunning, { count })
    );
  }

  if (status === 'running') {
    return withPreview(intl.formatMessage(toolCallLabelMessages.toolRunning), preview);
  }
  if (status === 'success') {
    return withPreview(intl.formatMessage(toolCallLabelMessages.toolRan), preview);
  }
  return withPreview(
    wrapTerminal(intl, status, intl.formatMessage(toolCallLabelMessages.toolGeneric)),
    preview
  );
};
